import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate } from '@tanstack/react-router'
import { ROUTES } from '@/constants/routeConstants'
import { ROUTE_ACCESS } from '@/constants/routeAccess.ts'
import { useAuth } from '@/hooks/useAuth.ts'
import { useLoginModal } from '@/contexts/LoginModalContext'
import { HeaderWelcome } from '@/components/layout/HeaderWelcome'

interface HeaderProps {
  isLight?: boolean
}

interface NavLink {
  key: string
  label: string
  route?: string
  children?: Array<NavLink>
}

export const Header = ({ isLight }: HeaderProps) => {
  const { t } = useTranslation('layout')
  const { isAuthenticated, logout, roles, user } = useAuth()
  const navigate = useNavigate()
  const { openLogin } = useLoginModal()
  const { pathname } = useLocation()

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [hoveredKey, setHoveredKey] = useState<string | null>(null)

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen)
  }

  const handleConnectionClick = async () => {
    if (isAuthenticated) {
      await logout()
      navigate({ to: ROUTES.ROOT })
    } else {
      openLogin()
    }
  }

  const canAccessRoute = (route: string): boolean => {
    const allowedRoles = ROUTE_ACCESS[route]
    return !allowedRoles || allowedRoles.some((role) => roles.includes(role))
  }

  const filterAccessibleLinks = (links: Array<NavLink>): Array<NavLink> =>
    links
      .filter((link) => !link.route || canAccessRoute(link.route))
      .map((link) => ({
        ...link,
        children: link.children
          ? filterAccessibleLinks(link.children)
          : undefined,
      }))

  const getNavigationLinks = (): Array<NavLink> => {
    if (!isAuthenticated) {
      return [
        {
          key: 'contactUs',
          label: t('header.contactUs'),
          route: ROUTES.CONTACT_US,
        },
        {
          key: 'recommendations',
          label: t('header.recommendations'),
          route: ROUTES.RECOMMEND,
        },
      ]
    }

    const authenticatedLinks: Array<NavLink> = [
      {
        key: 'consultants',
        label: t('header.consultants'),
        route: ROUTES.CONSULTANTS_LIST,
      },
      {
        key: 'myProfile',
        label: t('header.myProfile'),
        route: ROUTES.PROFILE,
      },
      {
        key: 'myNetwork',
        label: t('header.myNetwork'),
        route: ROUTES.MY_NETWORK,
      },
      {
        key: 'backoffice',
        label: t('header.backoffice'),
        route: ROUTES.BACKOFFICE,
        children: [
          {
            key: 'backoffice-invitations',
            label: t('header.invitations'),
            route: ROUTES.INVITE_STAFF,
          },
        ],
      },
      {
        key: 'admin',
        label: t('header.admin'),
        route: ROUTES.ADMIN,
      },
      {
        key: 'my-cooptations',
        label: t('header.myCooptations'),
        route: ROUTES.MY_COOPTATIONS,
      },
    ]

    return filterAccessibleLinks(authenticatedLinks)
  }

  const navigationLinks = getNavigationLinks()

  const isRouteActive = (route?: string): boolean =>
    !!route && pathname === route

  const isLinkActive = (link: NavLink): boolean =>
    isRouteActive(link.route) ||
    (link.children?.some((child) => isRouteActive(child.route)) ?? false)

  const activeClasses = (active: boolean) =>
    active
      ? `!font-semibold ${isLight ? '!border-black' : '!border-white'}`
      : ''

  // Le sous-menu ne s'affiche qu'au survol, même si sa page est active, et il
  // est ancré (position: absolute) sous son lien parent plutôt que rendu comme
  // un bandeau pleine largeur : sa taille suit son contenu (il grandira tout
  // seul si on ajoute "Utilisateurs"), et comme il sort du flux normal, il ne
  // fait jamais varier la hauteur du header (qui est en fixed). Traité comme
  // un vrai dropdown (coins arrondis partout, ombre, fond plein) plutôt que
  // comme une extension du bandeau, pour éviter l'effet "carton mal découpé".
  const renderNavLinks = (isMobile: boolean) => (
    <>
      {navigationLinks.map((link) => (
        <div key={link.key} className="relative">
          <Link
            to={link.route as string}
            onMouseEnter={
              isMobile
                ? undefined
                : () =>
                    setHoveredKey(
                      link.children && link.children.length > 0
                        ? link.key
                        : null,
                    )
            }
          >
            <span
              className={`${
                isMobile ? 'py-2 cursor-pointer' : 'py-1 cursor-pointer'
              } ${isLight ? 'dark-navlink' : 'light-navlink'} ${activeClasses(
                isLinkActive(link),
              )}`}
            >
              {link.label}
            </span>
          </Link>

          {!isMobile &&
            link.children &&
            link.children.length > 0 &&
            hoveredKey === link.key && (
              <div
                className={`absolute left-0 top-full mt-2 flex flex-row gap-6 whitespace-nowrap rounded-lg shadow-1 px-4 py-3 ${
                  isLight ? 'bg-white' : 'bg-gradient-orange-fuschia'
                }`}
              >
                {link.children.map((child) => (
                  <Link to={child.route as string} key={child.key}>
                    <span
                      className={`cursor-pointer ${
                        isLight ? 'dark-navlink' : 'light-navlink'
                      } ${activeClasses(isRouteActive(child.route))}`}
                    >
                      {child.label}
                    </span>
                  </Link>
                ))}
              </div>
            )}

          {isMobile && link.children && link.children.length > 0 && (
            <div className="flex flex-col pl-4 gap-2">
              {link.children.map((child) => (
                <Link to={child.route as string} key={child.key}>
                  <span
                    className={`py-1 cursor-pointer text-sm ${
                      isLight ? 'dark-navlink' : 'light-navlink'
                    } ${activeClasses(isRouteActive(child.route))}`}
                  >
                    {child.label}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>
      ))}
    </>
  )

  // Prénom de la session : absent si non connecté (rien n'est affiché).
  const welcomeFirstName = isAuthenticated ? (user?.firstName ?? '') : ''

  const connectionButton = (isMobile: boolean) => (
    <button
      className={`flex shrink-0 items-center justify-center whitespace-nowrap px-5 rounded-xl transition font-bold gap-2 ${
        isLight
          ? 'bg-gradient-orange-fuschia text-white'
          : 'bg-white text-black'
      } border border-transparent cursor-pointer hover:shadow-1 ${
        isMobile ? 'self-start w-full py-2 mt-2' : 'self-center w-fit py-1.5'
      }`}
      onClick={handleConnectionClick}
    >
      {t(isAuthenticated ? 'header.disconnection' : 'header.connection')}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="17"
        height="13"
        viewBox="0 0 17 13"
        fill="none"
      >
        <path
          d="M14.75 6.20703L15.4571 5.49992L16.1642 6.20703L15.4571 6.91414L14.75 6.20703ZM1 7.20703C0.447715 7.20703 0 6.75932 0 6.20703C0 5.65475 0.447715 5.20703 1 5.20703V6.20703V7.20703ZM9.25 0.707031L9.95711 -7.55191e-05L15.4571 5.49992L14.75 6.20703L14.0429 6.91414L8.54289 1.41414L9.25 0.707031ZM14.75 6.20703L15.4571 6.91414L9.95711 12.4141L9.25 11.707L8.54289 10.9999L14.0429 5.49992L14.75 6.20703ZM14.75 6.20703V7.20703H1V6.20703V5.20703H14.75V6.20703Z"
          fill="currentColor"
        />
      </svg>
    </button>
  )

  return (
    <header
      onMouseLeave={() => setHoveredKey(null)}
      className={`fixed z-50 w-full px-6 md:px-12 lg:px-8 xl:px-12 pt-[21.5px] pb-2 shadow-1 ${
        isLight ? 'bg-white' : 'bg-gradient-orange-fuschia'
      }`}
    >
      <div className="flex flex-row items-center">
        <Link to={ROUTES.ROOT}>
          <img
            className="block w-16 h-8 md:w-20 md:h-10"
            src={`/img/cleverlink-logo-${isLight ? 'color' : 'white'}.svg`}
            alt="Cleverlink"
          />
        </Link>

        {/* Navigation desktop à partir de `lg` seulement : connecté, elle compte
            jusqu'à 6 liens, qui ne tiennent pas sur une ligne entre md et lg
            (les libellés passaient sur deux lignes). En dessous, menu burger.
            whitespace-nowrap : un libellé ne se coupe jamais en deux. */}
        <div className="hidden lg:flex flex-row whitespace-nowrap text-base gap-4 xl:gap-6 2xl:gap-8 items-center ml-4 xl:ml-6 2xl:ml-10">
          {renderNavLinks(false)}
        </div>

        {/* Le message de bienvenue n'apparaît qu'à partir de `xl` : entre lg et
            xl, la navigation connectée (jusqu'à 6 liens) occupe déjà toute la
            largeur. Il reste visible dans le menu mobile. Le masquage est porté
            par ce conteneur : la pastille est elle-même en `flex`. */}
        <div className="hidden lg:flex ml-auto items-center gap-4 pl-4">
          <div className="hidden min-w-0 xl:flex">
            <HeaderWelcome firstName={welcomeFirstName} isLight={isLight} />
          </div>
          {connectionButton(false)}
        </div>

        <button
          className={`lg:hidden ml-auto flex flex-col justify-center items-center w-8 h-8 ${
            isLight ? 'text-black' : 'text-white'
          }`}
          onClick={toggleMobileMenu}
          aria-label="Toggle menu"
        >
          <span
            className={`block w-6 h-0.5 bg-current transition-transform duration-300 ${
              isMobileMenuOpen ? 'rotate-45 translate-y-1.5' : ''
            }`}
          />
          <span
            className={`block w-6 h-0.5 bg-current my-1 transition-opacity duration-300 ${
              isMobileMenuOpen ? 'opacity-0' : ''
            }`}
          />
          <span
            className={`block w-6 h-0.5 bg-current transition-transform duration-300 ${
              isMobileMenuOpen ? '-rotate-45 -translate-y-1.5' : ''
            }`}
          />
        </button>
      </div>

      <div
        className={`lg:hidden overflow-hidden transition-all duration-300 ${
          isMobileMenuOpen ? 'max-h-[28rem] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <nav
          className={`flex flex-col gap-4 pt-6 ${
            isLight ? 'text-black' : 'text-white'
          } text-base`}
        >
          {renderNavLinks(true)}
          <HeaderWelcome
            firstName={welcomeFirstName}
            isLight={isLight}
            className="mt-2 self-start"
          />
          {connectionButton(true)}
        </nav>
      </div>
    </header>
  )
}
